import React from "react";
import humanizeString from "humanize-string";
import { MenuItemType } from "@/libs/hooks/useMenu";
import { MenuItem } from "./MenuItem";

type MenuProps = {
  menu: MenuItemType[] | undefined;
  lang: string;
  isLoading: boolean;
};

type GroupedMenuItems = {
  [key: string]: MenuItemType[];
};

const Menu = ({ menu, lang, isLoading }: MenuProps) => {
  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center py-16"
        aria-live="assertive"
        aria-busy="true"
      >
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-vintage-cream/30 border-t-vintage-cream rounded-full animate-spin" />
          <span className="font-serif text-vintage-cream text-lg">
            Loading menu...
          </span>
        </div>
      </div>
    );
  }

  if (!menu) {
    return (
      <div
        role="alert"
        className="text-center py-16 font-serif text-vintage-cream text-lg"
      >
        Something went wrong...
      </div>
    );
  }

  const groupedItems = menu
    .filter((item) => item.attributes.showOnMainPage)
    .sort((a, b) => a.id - b.id)
    .reduce<GroupedMenuItems>((acc, item) => {
      const type = item.attributes.type;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(item);
      return acc;
    }, {});

  return (
    <div className="space-y-16">
      {Object.keys(groupedItems).map((type, typeIndex) => (
        <section key={type} id={`menu-${type}`} className="relative">
          {/* Category header with vintage styling */}
          <div className="text-center mb-10">
            {/* Decorative divider */}
            <div className="flex items-center justify-center gap-6 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rotate-45 border border-vintage-cream/40" />
                <div className="w-16 h-px bg-vintage-cream/40" />
              </div>
              <span className="text-vintage-ochre text-xl">✦</span>
              <div className="flex items-center gap-2">
                <div className="w-16 h-px bg-vintage-cream/40" />
                <div className="w-3 h-3 rotate-45 border border-vintage-cream/40" />
              </div>
            </div>

            <h3 className="font-display text-4xl md:text-5xl font-bold text-vintage-cream tracking-wide uppercase">
              {humanizeString(type)}
            </h3>

            {/* Decorative underline */}
            <div className="flex items-center justify-center gap-3 mt-4">
              <div className="w-8 h-px bg-vintage-ochre/60" />
              <div className="w-2 h-2 bg-vintage-ochre/60 rotate-45" />
              <div className="w-8 h-px bg-vintage-ochre/60" />
            </div>
          </div>

          {/* Menu items grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {groupedItems[type].map((item, index) => (
              <MenuItem
                key={item.id}
                {...item.attributes}
                ingredients={item.attributes?.ingredientsJson?.[lang]}
                animationDelay={index * 0.1}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export { Menu };
